// Client for the WoodWing Image API (POST /images): turns article names into
// signed CDN links for a thumbnail and a preview. The images themselves load
// straight from WoodWing's CDN via <img src>; only the lookup goes through here.
//
// Deliberately plain `fetch` instead of the axios `agent`: that one puts the
// Laravel bearer token on every request, and it must not leak to this service.
//
// The links are opaque keys to the image: use them as-is, don't log them, and
// don't keep them longer than CACHE_TTL_MS (the API guarantees >= 2 hours).

//#region TYPES
export interface WoodWingImage {
  thumbnail: string;
  preview: string;
}
//#endregion

//#region CONFIG
// Unset (e.g. production until the API runs on Azure) = feature off: no
// requests, and the week-order list renders exactly as before.
const BASE_URL = (process.env.REACT_APP_WOODWING_IMAGE_API_URL ?? "")
  .trim()
  .replace(/\/+$/, "");
export const woodWingImagesEnabled = BASE_URL !== "";

const MAX_TERMS_PER_REQUEST = 200;
const CACHE_TTL_MS = 90 * 60 * 1000;
// After a 5xx / network error: 30s, doubling per consecutive failure, max 5min.
const FAILURE_COOLDOWN_MS = 30 * 1000;
const MAX_FAILURE_COOLDOWN_MS = 5 * 60 * 1000;
// Used on a 429 when Retry-After can't be read (cross-origin, the API has to
// expose that header explicitly).
const FALLBACK_RETRY_AFTER_SECONDS = 60;
// 1-200 chars, no quotes, backslashes or control characters. The API answers
// null for anything else, so those never need to cost a request.
// eslint-disable-next-line no-control-regex -- matching control chars is the point
const VALID_TERM = /^[^"\\\u0000-\u001f\u007f]{1,200}$/;
//#endregion

//#region STATE (lives as long as the page)
// null = looked up, no image (not an error). Missing = not looked up yet.
const cache = new Map<string, { image: WoodWingImage | null; expiresAt: number }>();
const inFlight = new Map<string, Promise<void>>();
// No new requests before this moment (rate limit or WoodWing down).
let retryAt = 0;
let consecutiveFailures = 0;
//#endregion

//#region PUBLIC
// undefined = not known (yet), null = no image, otherwise the links.
export function getWoodWingImage(
  term: string | null | undefined,
): WoodWingImage | null | undefined {
  const key = term?.trim() ?? "";
  if (!key) return null;
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (entry.expiresAt <= Date.now()) {
    cache.delete(key);
    return undefined;
  }
  return entry.image;
}

// When the next request may go out; 0 or in the past = now.
export function getWoodWingRetryAt(): number {
  return retryAt;
}

// Looks up every term that isn't cached or already on its way, in as few
// requests as possible (one per 200 terms). Never rejects: failures leave the
// terms uncached and set `retryAt`, so callers show placeholders and retry.
export async function loadWoodWingImages(terms: string[]): Promise<void> {
  if (!woodWingImagesEnabled) return;

  const toFetch: string[] = [];
  const waitFor: Promise<void>[] = [];
  for (const term of new Set(terms.map((t) => t.trim()))) {
    if (getWoodWingImage(term) !== undefined) continue;
    if (!VALID_TERM.test(term)) {
      remember(term, null);
      continue;
    }
    const pending = inFlight.get(term);
    if (pending) waitFor.push(pending);
    else toFetch.push(term);
  }

  if (toFetch.length > 0 && Date.now() >= retryAt) {
    for (let i = 0; i < toFetch.length; i += MAX_TERMS_PER_REQUEST) {
      const chunk = toFetch.slice(i, i + MAX_TERMS_PER_REQUEST);
      const request = postImages(chunk).finally(() =>
        chunk.forEach((term) => inFlight.delete(term)),
      );
      chunk.forEach((term) => inFlight.set(term, request));
      waitFor.push(request);
    }
  }

  await Promise.all(waitFor);
}
//#endregion

//#region INTERNALS
async function postImages(terms: string[]): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}/images`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ searchTerms: terms }),
      credentials: "omit",
    });
  } catch {
    // Network error, API not running, or the origin isn't allowed (CORS).
    registerFailure();
    return;
  }

  if (response.status === 429) {
    retryAt = Math.max(retryAt, Date.now() + retryAfterMs(response));
    return;
  }
  // 502 = WoodWing unreachable; any other 5xx is just as temporary.
  if (response.status >= 500) {
    registerFailure();
    return;
  }
  // 400 / 413 can't happen with chunks of 200 valid terms, and retrying won't
  // fix them: settle these terms as "no image".
  if (!response.ok) {
    terms.forEach((term) => remember(term, null));
    return;
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    registerFailure();
    return;
  }

  consecutiveFailures = 0;
  // Every requested term comes back as a key, exactly as sent.
  const results = (body ?? {}) as Record<string, unknown>;
  terms.forEach((term) =>
    remember(
      term,
      Object.prototype.hasOwnProperty.call(results, term)
        ? toImage(results[term])
        : null,
    ),
  );
}

function toImage(value: unknown): WoodWingImage | null {
  if (!value || typeof value !== "object") return null;
  const { thumbnail, preview } = value as Partial<WoodWingImage>;
  const small = typeof thumbnail === "string" && thumbnail ? thumbnail : null;
  const large = typeof preview === "string" && preview ? preview : null;
  if (!small && !large) return null;
  return { thumbnail: (small ?? large)!, preview: (large ?? small)! };
}

function remember(term: string, image: WoodWingImage | null) {
  cache.set(term, { image, expiresAt: Date.now() + CACHE_TTL_MS });
}

function registerFailure() {
  consecutiveFailures += 1;
  const cooldown = Math.min(
    FAILURE_COOLDOWN_MS * 2 ** (consecutiveFailures - 1),
    MAX_FAILURE_COOLDOWN_MS,
  );
  retryAt = Math.max(retryAt, Date.now() + cooldown);
}

// Retry-After is either seconds or an HTTP date; +1s margin.
function retryAfterMs(response: Response): number {
  const header = response.headers.get("Retry-After")?.trim();
  if (header) {
    const seconds = Number(header);
    if (Number.isFinite(seconds)) return (seconds + 1) * 1000;
    const date = Date.parse(header);
    if (!Number.isNaN(date)) return Math.max(0, date - Date.now()) + 1000;
  }
  return FALLBACK_RETRY_AFTER_SECONDS * 1000;
}
//#endregion
